import { CONFIGURATION_STATUS_TYPE } from 'src/shared/constants/constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Form } from 'react-final-form'
import { Alert, Grid2, Stack, Typography } from '@mui/material'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { required } from 'src/shared/form/validation/validators'
import { TextField } from 'src/shared/form/control/TextField'
import { Option } from 'src/shared/form/control'
import { useMemo } from 'react'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { Feature_Type_Config } from 'src/@types/graphql'
import { useUserLocale } from 'src/user/UserContext'
import { mapFeatureTypesToOptions } from 'src/screens/administration/configuration-management/features-configuration/FeaturesConfigurationUtils'
import { getConfigStatusOptions } from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { createDecorators } from 'src/shared/form/utils/decorators'

export interface FeatureGroupsConfigFormValues {
  enName: string
  deName: string
  frName: string
  itName: string
  status: CONFIGURATION_STATUS_TYPE
  featureTypeId: number | null
}

interface FeatureGroupsConfigFormProps {
  mode: 'EDIT' | 'CREATE'
  onSubmit: (values: FeatureGroupsConfigFormValues) => Promise<void>
  initialValues: FeatureGroupsConfigFormValues
  featureTypes: Feature_Type_Config[]
  formDirty?: boolean
}

export const FeaturesConfigurationFeatureGroupsForm = ({
  mode,
  onSubmit,
  initialValues,
  featureTypes,
  formDirty,
}: FeatureGroupsConfigFormProps) => {
  const { getMessage } = useMessageSource()
  const locale = useUserLocale()
  const decorators = createDecorators()

  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])

  const featureTypeOptions = useMemo(
    () => mapFeatureTypesToOptions(featureTypes, getMessage, locale),
    [featureTypes, getMessage, locale],
  )

  return (
    <Stack spacing={2}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      <Form<FeatureGroupsConfigFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        decorators={decorators}
        render={({ handleSubmit }) => {
          return (
            <form id="feature-group-config-form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={4}>
                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={statusFieldOptions}
                      name="status"
                      label={getMessage('label.status')}
                      disableClearable
                      required
                      validate={required()}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={featureTypeOptions}
                      name="featureTypeId"
                      label={getMessage('label.feature.type')}
                      disabled={mode === 'EDIT'}
                      disableClearable
                      required
                      validate={required()}
                    />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">{getMessage('label.configuration.features.feature.group.name')}</Typography>

                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <TextField name="deName" label="DE" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="enName" label="EN" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="frName" label="FR" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="itName" label="IT" required validate={required()} />
                  </Grid2>
                </Grid2>
                <DirtyFormSpy gridDirty={formDirty} />
              </Stack>
            </form>
          )
        }}
      />
    </Stack>
  )
}
